import {
    flattenModuleNavLeaves,
    moduleNavTree,
    type ModuleNavGroup,
    type ModuleNavLeaf,
} from "../../routes/moduleNavigation";
import { navHrefBasePath } from "../../routes/moduleNavigation.utils";
import { routePaths } from "../../routes/navigation";
import { canAccessNavLeafForMenu } from "./navLeafMenuAccess";
import { canAccessPath } from "./pathAccess";
import type { User } from "./types";

export {
    canAccessPath,
    hasAnyPermission,
    hasPermission,
} from "./pathAccess";

/** Роли/права с пълен B2B и клиентски обхват (без портфолио на sales_rep). */
const portfolioViewAllRoles = ["administrator", "sales_supervisor", "management"] as const;
const portfolioViewAllPermissions = ["clients.view_all", "dashboard.management"] as const;

export function canAccessNavHref(user: User | null, href: string) {
    return canAccessPath(user, navHrefBasePath(href));
}

function isMessagesNavHref(href: string) {
    return navHrefBasePath(href) === routePaths.messages;
}

/**
 * Съобщенията са винаги достъпни по URL, но не трябва да държат модул
 * (Глобално / Магазини / Онлайн / Склад) видим без други права.
 */
function filterNavLeaves(user: User | null, leaves: ModuleNavLeaf[]) {
    const visible: ModuleNavLeaf[] = [];
    const deferredMessages: ModuleNavLeaf[] = [];

    for (const leaf of leaves) {
        if (leaf.children?.length) {
            const children = filterNavLeaves(user, leaf.children);

            if (children.length > 0) {
                visible.push({ ...leaf, children });
            }

            continue;
        }

        if (!leaf.href || !canAccessNavLeafForMenu(user, leaf)) {
            continue;
        }

        if (isMessagesNavHref(leaf.href)) {
            deferredMessages.push(leaf);
            continue;
        }

        visible.push(leaf);
    }

    if (visible.length > 0) {
        visible.push(...deferredMessages);
    }

    return visible;
}

export function getVisibleModuleNavTree(user: User | null): ModuleNavGroup[] {
    if (!user || !user.isActive) {
        return [];
    }

    // Менюто следва само effective permissions (роля ∖ matrix + user grants) — без role allowlist.
    return moduleNavTree
        .map((group) => ({
            ...group,
            children: filterNavLeaves(user, group.children),
        }))
        .filter((group) => group.children.length > 0);
}

export function getDefaultAuthorizedPath(user: User | null) {
    if (!user || !user.isActive) {
        return routePaths.login;
    }

    const firstLeaf = flattenModuleNavLeaves(getVisibleModuleNavTree(user))[0];

    return firstLeaf?.href ? navHrefBasePath(firstLeaf.href) : routePaths.dashboard;
}

export function getVisibleWorkspaceNavItems(user: User | null) {
    const seenPaths = new Set<string>();

    return flattenModuleNavLeaves(getVisibleModuleNavTree(user))
        .filter((leaf): leaf is typeof leaf & { href: string } => Boolean(leaf.href))
        .filter((leaf) => {
            const basePath = navHrefBasePath(leaf.href);

            if (seenPaths.has(basePath)) {
                return false;
            }

            seenPaths.add(basePath);

            return true;
        })
        .map((leaf) => ({
            path: navHrefBasePath(leaf.href),
            label: leaf.label,
            description: leaf.description ?? "",
        }));
}

export function canViewAllSalesPortfolio(user: User | null) {
    if (!user || !user.isActive) {
        return false;
    }

    if (portfolioViewAllRoles.includes(user.role as (typeof portfolioViewAllRoles)[number])) {
        return true;
    }

    return user.permissions.some((code) =>
        (portfolioViewAllPermissions as readonly string[]).includes(code),
    );
}

export function canManageClientProfileLinks(user: User | null) {
    if (!user || !user.isActive) {
        return false;
    }

    return (
        user.role === "administrator"
        || user.role === "sales_supervisor"
        || user.role === "management"
    );
}

/** Назначаване доставчик ↔ баер (едро): админ + Диляна / Тихомира. */
export function canAssignSupplierBuyer(user: User | null) {
    if (!user || !user.isActive) {
        return false;
    }

    if (user.role === "administrator") {
        return true;
    }

    const normalized = user.name.trim().toLowerCase().replace(/\s+/g, " ");

    return (
        normalized === "диляна атанасова"
        || normalized === "тихомира башева"
        || normalized === "тихомира бошева"
    );
}
